import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Clock, FileText, Package, Search, QrCode, Plus, Trash2,
  Tag, AlertTriangle, Pen, Camera, Image as ImageIcon, Shield, XCircle, History, Eye,
  Paperclip,
} from 'lucide-react';
import AttachmentList from '../components/shared/AttachmentList';
import ImageLightbox from '../components/shared/ImageLightbox';
import { useQrScanner } from '../hooks/useQrScanner';
import { useIsMobile } from '../hooks/useIsMobile';
import { parseBinQrCode } from '../components/ui/bin-article-selector';
import MobileCloseWizard from '../components/tickets/MobileCloseWizard';
import TicketMobileActionBar from '../components/tickets/TicketMobileActionBar';
import TicketActionsCard from '../components/tickets/TicketActionsCard';
import TicketInfoSidebar from '../components/tickets/TicketInfoSidebar';
import TicketStatusTimeline from '../components/tickets/TicketStatusTimeline';
import TicketDialogs from '../components/tickets/TicketDialogs';

const STATUS_CONFIG = {
  open: { label: 'Deschis', color: 'bg-blue-500 text-white', icon: Clock },
  accepted: { label: 'Acceptat', color: 'bg-amber-500 text-white', icon: Clock },
  in_progress: { label: 'In Lucru', color: 'bg-purple-500 text-white', icon: Clock },
  waiting_parts: { label: 'Asteapta Piese', color: 'bg-orange-500 text-white', icon: Clock },
  monitoring: { label: 'Sub Monitorizare', color: 'bg-yellow-500 text-slate-900', icon: Eye },
  resolved: { label: 'Rezolvat', color: 'bg-emerald-500 text-white', icon: Clock },
  closed: { label: 'Inchis', color: 'bg-slate-500 text-white', icon: Shield },
  rejected: { label: 'Respins', color: 'bg-red-500 text-white', icon: XCircle },
  transformed: { label: 'Transformat', color: 'bg-cyan-500 text-white', icon: Clock }
};

const PRIORITY_CONFIG = {
  critical: { label: 'Critic', color: 'bg-red-600 text-white' },
  urgent: { label: 'Urgent', color: 'bg-orange-500 text-white' },
  high: { label: 'Ridicat', color: 'bg-amber-500 text-white' },
  medium: { label: 'Mediu', color: 'bg-yellow-400 text-slate-900' },
  low: { label: 'Scazut', color: 'bg-green-500 text-white' }
};

const SLA_CONFIG = {
  ok: { label: 'In termen', color: 'text-green-600', bg: 'bg-green-50 border-green-200' },
  at_risk: { label: 'Risc', color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
  breached: { label: 'Depasit', color: 'text-red-600', bg: 'bg-red-50 border-red-200' }
};

const TicketDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasRole, hasPermission } = useAuth();
  const { plants, locations } = useData();
  const isMobile = useIsMobile();

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [galleryLightboxIdx, setGalleryLightboxIdx] = useState(null);
  const [searchResults, setSearchResults] = useState([]);

  // Dialog states
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [showTransformDialog, setShowTransformDialog] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [showMobileWizard, setShowMobileWizard] = useState(false);
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [showReviseDialog, setShowReviseDialog] = useState(false);

  // Form states
  const [rejectData, setRejectData] = useState({ rejection_reason: '', notes: '' });
  const [statusChangeData, setStatusChangeData] = useState({ status: '', notes: '' });
  const [reassignData, setReassignData] = useState({ new_assignee_id: '', notes: '' });
  const [transformData, setTransformData] = useState({ scheduled_date: '', procedure_id: '', assigned_to: '', estimated_hours: '' });
  const [closeData, setCloseData] = useState({ notes: '', operatorSkipped: false, skipReason: '' });
  const [reviseData, setReviseData] = useState({ reason: '', notes: '' });
  const [reviseConsumedItems, setReviseConsumedItems] = useState([]);
  const [reviseClosureImages, setReviseClosureImages] = useState([]);

  // Parts ETA flow
  const [showPartsEtaDialog, setShowPartsEtaDialog] = useState(false);
  const [partsEtaData, setPartsEtaData] = useState({ days: '' });
  const [partsEtaMode, setPartsEtaMode] = useState('create'); // 'create' | 'edit'

  // Consumed items
  const [consumedItems, setConsumedItems] = useState([]);
  const [articleSearch, setArticleSearch] = useState('');
  const [pendingArticle, setPendingArticle] = useState(null);
  const [pendingQty, setPendingQty] = useState(1);
  const [showLotSelector, setShowLotSelector] = useState(false);
  const [availableLots, setAvailableLots] = useState([]);
  const [selectedLot, setSelectedLot] = useState(null);
  const [lotsLoading, setLotsLoading] = useState(false);
  const [binScanOpen, setBinScanOpen] = useState(false);
  const [binScanCode, setBinScanCode] = useState('');
  const [binScanType, setBinScanType] = useState('bin');

  const { start: startQrHook, stop: stopQrHook } = useQrScanner();
  const technicians = users.filter(u => ['technician', 'manager', 'admin'].includes(u.role));

  const fetchTicket = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/tickets/${id}`);
      setTicket(response.data);
      setConsumedItems(response.data.consumed_items || []);
    } catch {
      toast.error('Tichetul nu a fost gasit');
      navigate('/tickets');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchTicket(); fetchUsers(); fetchProcedures(); }, [fetchTicket]);

  const fetchUsers = async () => { try { const r = await api.get('/users'); setUsers(r.data.filter(u => u.is_active !== false)); } catch {} };
  const fetchProcedures = async () => { try { const r = await api.get('/procedures'); setProcedures(r.data); } catch {} };

  const getAvailableStatuses = (currentStatus) => {
    const transitions = {
      open: ['accepted', 'rejected'], accepted: ['in_progress', 'rejected'],
      in_progress: ['waiting_parts', 'monitoring', 'resolved', 'accepted'],
      waiting_parts: ['in_progress', 'resolved'], monitoring: ['in_progress', 'resolved', 'waiting_parts'],
      resolved: ['closed', 'in_progress'], rejected: ['open'], closed: [], transformed: []
    };
    return transitions[currentStatus] || [];
  };

  // Action handlers
  const handleReject = async () => {
    if (!rejectData.rejection_reason) { toast.error('Motivul respingerii este obligatoriu'); return; }
    try { await api.post(`/tickets/${id}/reject`, rejectData); toast.success('Tichet respins'); setShowRejectDialog(false); fetchTicket(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Eroare'); }
  };

  const handleStatusChange = async () => {
    if (!statusChangeData.status) { toast.error('Selectati un status'); return; }
    try { await api.post(`/tickets/${id}/change-status`, null, { params: { status: statusChangeData.status, notes: statusChangeData.notes || null } }); toast.success('Status actualizat!'); setShowStatusDialog(false); fetchTicket(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Eroare'); }
  };

  const quickStatusChange = async (newStatus) => {
    // Intercept waiting_parts: requires parts_eta_days (mandatory)
    if (newStatus === 'waiting_parts') {
      setPartsEtaData({ days: '' });
      setPartsEtaMode('create');
      setShowPartsEtaDialog(true);
      return;
    }
    try { await api.post(`/tickets/${id}/change-status`, null, { params: { status: newStatus } }); toast.success(`Status: ${STATUS_CONFIG[newStatus]?.label}`); fetchTicket(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Eroare la schimbare status'); }
  };

  const handlePartsEtaConfirm = async () => {
    const days = parseInt(partsEtaData.days, 10);
    if (!days || days <= 0) { toast.error('Introduceti un numar valid de zile (minim 1)'); return; }
    if (days > 365) { toast.error('Termenul nu poate depasi 365 de zile'); return; }
    try {
      if (partsEtaMode === 'edit') {
        await api.post(`/tickets/${id}/parts-eta`, null, { params: { parts_eta_days: days } });
        toast.success(`Termen actualizat: ${days} zile`);
      } else {
        await api.post(`/tickets/${id}/change-status`, null, { params: { status: 'waiting_parts', parts_eta_days: days } });
        toast.success(`Status: Asteapta Piese - ETA ${days} zile`);
      }
      setShowPartsEtaDialog(false);
      setPartsEtaData({ days: '' });
      fetchTicket();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare la salvare termen'); }
  };

  const openEditPartsEta = () => {
    setPartsEtaData({ days: String(ticket?.parts_eta_days || '') });
    setPartsEtaMode('edit');
    setShowPartsEtaDialog(true);
  };

  const quickAccept = async () => {
    try { await api.post(`/tickets/${id}/accept`, { priority_assigned: ticket?.priority_requested || 'medium', assigned_to_ids: [], notes: '' }); toast.success('Tichet acceptat si asignat!'); fetchTicket(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Eroare la acceptare'); }
  };

  const handleReassign = async () => {
    if (!reassignData.new_assignee_id) { toast.error('Selectati un tehnician'); return; }
    try { await api.post(`/tickets/${id}/reassign`, null, { params: { new_assignee_id: reassignData.new_assignee_id, notes: reassignData.notes || null } }); toast.success('Tichet reasignat!'); setShowReassignDialog(false); fetchTicket(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Eroare'); }
  };

  const handleTransform = async () => {
    try {
      const payload = { ...transformData, scheduled_date: new Date(transformData.scheduled_date).toISOString(), estimated_hours: transformData.estimated_hours ? parseFloat(transformData.estimated_hours) : null };
      await api.post(`/tickets/${id}/transform`, payload); toast.success('Tichet transformat in revizie!'); setShowTransformDialog(false); fetchTicket();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare'); }
  };

  const handleClose = async (payload) => {
    try { await api.post(`/tickets/${id}/close`, payload); toast.success('Tichet inchis cu succes!'); setShowCloseDialog(false); fetchTicket(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Eroare la inchidere'); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Sigur doriti sa stergeti acest tichet?')) return;
    try { await api.delete(`/tickets/${ticket.id}`); toast.success('Tichet sters'); navigate('/tickets'); }
    catch { toast.error('Eroare la stergere'); }
  };

  // Consumed items handlers
  const showAddArticleDialog = async (article) => {
    setPendingArticle(article); setPendingQty(1); setSelectedLot(null);
    try {
      setLotsLoading(true);
      const res = await api.get(`/supplier-intelligence/articles/${article.id}/available-lots`);
      setAvailableLots(res.data.lots || []);
      if (res.data.has_multiple_suppliers && res.data.lots.length > 1) setShowLotSelector(true);
      else if (res.data.lots.length === 1) setSelectedLot(res.data.lots[0]);
    } catch { setAvailableLots([]); } finally { setLotsLoading(false); }
  };

  const confirmAddArticle = async (overrideQty) => {
    if (!pendingArticle) return;
    const qty = overrideQty || pendingQty;
    if (qty <= 0) return;
    const newItem = {
      article_id: pendingArticle.id, article_name: pendingArticle.name, article_code: pendingArticle.code,
      quantity: qty, unit: pendingArticle.unit || 'buc', lot_number: selectedLot?.lot_number || null,
      supplier_id: selectedLot?.supplier_id || null, supplier_name: selectedLot?.supplier_name || null, unit_price: selectedLot?.unit_price || 0,
    };
    try { const r = await api.post(`/tickets/${id}/add-consumed-item`, newItem); setConsumedItems(r.data.consumed_items); setArticleSearch(''); toast.success(`${pendingArticle.name} x${qty} adaugat`); }
    catch { toast.error('Eroare la adaugare'); }
    setPendingArticle(null); setSelectedLot(null); setShowLotSelector(false);
  };

  const handleRemoveConsumedItem = async (articleId) => {
    try { const r = await api.delete(`/tickets/${id}/consumed-item/${articleId}`); setConsumedItems(r.data.consumed_items); }
    catch { toast.error('Eroare la stergere'); }
  };

  const handleUpdateConsumedQty = async (articleId, newQty) => {
    if (newQty < 0.5) return;
    setConsumedItems(prev => prev.map(item => item.article_id === articleId ? { ...item, quantity: newQty } : item));
    const item = consumedItems.find(i => i.article_id === articleId);
    if (item) { try { await api.delete(`/tickets/${id}/consumed-item/${articleId}`); await api.post(`/tickets/${id}/add-consumed-item`, { ...item, quantity: newQty }); } catch {} }
  };

  // QR Scanner
  const startQrScanner = async () => {
    setShowQrScanner(true);
    setTimeout(async () => {
      try {
        await startQrHook('ticket-qr-reader', async (decodedText) => {
          await stopQrHook(); setShowQrScanner(false);
          const binResult = parseBinQrCode(decodedText);
          if (binResult.isBin) { setBinScanCode(binResult.value); setBinScanType(binResult.type); setBinScanOpen(true); return; }
          let searchCode = decodedText;
          const parts = decodedText.split(':');
          if (parts.length >= 2 && parts[0] === 'ART') searchCode = parts.slice(1).join(':');
          try { const resp = await api.get('/articles', { params: { search: searchCode } }); if (resp.data.length > 0) { showAddArticleDialog(resp.data[0]); toast.success(`Articol scanat: ${resp.data[0].name}`); } else toast.error(`Articol negasit: ${searchCode}`); }
          catch { toast.error('Eroare la cautare articol'); }
        }, { fps: 10, qrbox: { width: 250, height: 250 } });
      } catch { toast.error('Nu s-a putut porni camera'); setShowQrScanner(false); }
    }, 300);
  };

  const handleBinArticleSelect = (art) => {
    setBinScanOpen(false);
    showAddArticleDialog({ id: art.article_id, name: art.article_name, code: art.article_code, unit: art.unit });
    toast.success(`Articol selectat: ${art.article_name}`);
  };

  // Revise
  const openReviseDialog = () => {
    setReviseData({ reason: '', notes: '' });
    setReviseConsumedItems([...(ticket?.consumed_items || [])]);
    setReviseClosureImages((ticket?.closure_images || []).map((url, i) => ({ id: i, preview: url, path: url })));
    setShowReviseDialog(true);
  };

  const handleRevise = async () => {
    if (!reviseData.reason.trim()) { toast.error('Motivul revizuirii este obligatoriu'); return; }
    try {
      const closureImgs = reviseClosureImages.map(img => img.path || img.base64);
      await api.post(`/tickets/${id}/revise`, { reason: reviseData.reason, notes: reviseData.notes || null, consumed_items: reviseConsumedItems, closure_images: closureImgs });
      toast.success('Tichet revizuit cu succes!'); setShowReviseDialog(false); fetchTicket();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare la revizuire'); }
  };

  // Article search
  useEffect(() => {
    if (articleSearch.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => { try { const r = await api.get('/articles', { params: { search: articleSearch } }); setSearchResults(r.data); } catch { setSearchResults([]); } }, 300);
    return () => clearTimeout(t);
  }, [articleSearch]);

  if (loading) return <div className="flex items-center justify-center py-24"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div></div>;
  if (!ticket) return null;

  const statusCfg = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
  const priorityCfg = PRIORITY_CONFIG[ticket.priority_assigned || ticket.priority_requested] || PRIORITY_CONFIG.medium;
  const slaCfg = SLA_CONFIG[ticket.sla_status] || SLA_CONFIG.ok;
  const StatusIcon = statusCfg.icon;
  const isEditable = !['closed', 'transformed'].includes(ticket.status);

  return (
    <div className="space-y-4 lg:space-y-6 max-w-6xl mx-auto pb-32 lg:pb-0" data-testid="ticket-detail-page">
      {/* Mobile Action Bar */}
      <TicketMobileActionBar
        ticket={ticket} hasRole={hasRole} hasPermission={hasPermission}
        onQuickAccept={quickAccept} onQuickStatusChange={quickStatusChange}
        onReject={() => { setRejectData({ rejection_reason: '', notes: '' }); setShowRejectDialog(true); }}
        onReassign={() => { setReassignData({ new_assignee_id: '', notes: '' }); setShowReassignDialog(true); }}
        onOpenMobileWizard={() => setShowMobileWizard(true)}
        onRevise={openReviseDialog}
        onDelete={handleDelete}
      />

      {/* Back + Header */}
      <div className="flex items-start gap-2 sm:gap-3">
        <Button variant="ghost" size="icon" className="shrink-0 mt-1" onClick={() => navigate(-1)} data-testid="back-to-tickets-btn"><ArrowLeft className="w-5 h-5" /></Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm text-slate-400 bg-slate-100 px-2 py-0.5 rounded" data-testid="ticket-internal-number">#{(ticket.id || '').slice(0, 8).toUpperCase()}</span>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 break-words">{ticket.title}</h1>
            <Badge className={statusCfg.color} data-testid="ticket-status-badge"><StatusIcon className="w-3.5 h-3.5 mr-1" />{statusCfg.label}</Badge>
            {ticket.status === 'waiting_parts' && ticket.parts_eta_days && (
              <Badge className="bg-orange-100 text-orange-800 border border-orange-300" data-testid="ticket-eta-badge">
                <Package className="w-3 h-3 mr-1" />
                ETA: {ticket.parts_eta_days}z
                {ticket.parts_eta_date && (
                  <span className="ml-1 text-orange-600"> · {new Date(ticket.parts_eta_date).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short' })}</span>
                )}
              </Badge>
            )}
            <Badge className={priorityCfg.color} data-testid="ticket-priority-badge">{priorityCfg.label}</Badge>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Creat de <span className="font-medium">{ticket.created_by_name || 'N/A'}</span> la {new Date(ticket.created_at).toLocaleString('ro-RO')}
            {ticket.reporter_name && <span className="ml-2">| Raportat de <span className="font-medium">{ticket.reporter_name}</span></span>}
          </p>
        </div>
      </div>

      {/* SLA Banner */}
      {ticket.sla_deadline && ticket.status !== 'closed' && (
        <div className={`flex items-center gap-3 p-3 rounded-lg border ${slaCfg.bg}`} data-testid="sla-banner">
          <AlertTriangle className={`w-5 h-5 ${slaCfg.color}`} />
          <div>
            <span className={`font-semibold text-sm ${slaCfg.color}`}>SLA: {slaCfg.label}</span>
            <span className="text-sm text-slate-600 ml-2">Termen: {new Date(ticket.sla_deadline).toLocaleString('ro-RO')}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* LEFT: Main Info */}
        <div className="lg:col-span-2 space-y-4 lg:space-y-6">
          {/* Description */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4 text-slate-500" /> Descriere Problema</CardTitle></CardHeader>
            <CardContent>
              <p className="text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="ticket-description">{ticket.description}</p>
              {ticket.symptom_name && (
                <div className="mt-4 flex items-center gap-2">
                  <Tag className="w-4 h-4 text-amber-500" /><span className="text-sm text-slate-500">Simptom:</span>
                  <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50">{ticket.symptom_name}</Badge>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Images */}
          {ticket.images?.length > 0 && (
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><ImageIcon className="w-4 h-4 text-slate-500" /> Imagini Atasate ({ticket.images.length})</CardTitle></CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {ticket.images.map((img, index) => (
                    <button
                      type="button"
                      key={index}
                      onClick={() => setGalleryLightboxIdx(index)}
                      className="rounded-lg overflow-hidden border hover:ring-2 hover:ring-indigo-400 transition-all"
                      data-testid={`ticket-image-thumb-${index}`}
                    >
                      <img
                        src={img.startsWith('http') ? img : `${process.env.REACT_APP_BACKEND_URL}${img}`}
                        alt={`Imagine ${index + 1}`}
                        className="w-28 h-28 object-cover"
                      />
                    </button>
                  ))}
                </div>
              </CardContent>
              {galleryLightboxIdx !== null && (
                <ImageLightbox
                  images={ticket.images.map((url, i) => ({ url, name: `Imagine ${i + 1}` }))}
                  index={galleryLightboxIdx}
                  onClose={() => setGalleryLightboxIdx(null)}
                  onIndexChange={setGalleryLightboxIdx}
                />
              )}
            </Card>
          )}

          {/* Non-image attachments (PDFs, Word, Excel, video etc.) */}
          {(ticket.attachments || []).filter(a => a.type !== 'image' && !(a.content_type || '').startsWith('image/')).length > 0 && (
            <Card data-testid="ticket-attachments-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Paperclip className="w-4 h-4 text-indigo-600" />
                  Documente si alte fisiere (
                  {(ticket.attachments || []).filter(a => a.type !== 'image' && !(a.content_type || '').startsWith('image/')).length}
                  )
                </CardTitle>
              </CardHeader>
              <CardContent>
                <AttachmentList
                  attachments={(ticket.attachments || []).filter(a => a.type !== 'image' && !(a.content_type || '').startsWith('image/'))}
                  showPreview={false}
                  testIdPrefix="ticket-attachment"
                />
              </CardContent>
            </Card>
          )}

          {/* Consumed Materials (editable) */}
          {isEditable && (
            <Card data-testid="consumed-materials-card">
              <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Package className="w-4 h-4 text-blue-500" /> Materiale Consumate</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input value={articleSearch} onChange={(e) => setArticleSearch(e.target.value)} placeholder="Cauta articol dupa nume sau cod..." className="pl-10 h-10" data-testid="article-search-input" />
                  </div>
                  <Button variant="outline" size="icon" className="h-10 w-10 shrink-0" onClick={startQrScanner} data-testid="scan-qr-btn"><QrCode className="w-4 h-4" /></Button>
                </div>
                {searchResults.length > 0 && (
                  <div className="border rounded-lg divide-y bg-white max-h-80 overflow-y-auto shadow-sm">
                    {searchResults.map(article => (
                      <div key={article.id} onClick={() => { showAddArticleDialog(article); setSearchResults([]); setArticleSearch(''); }}
                        className="p-3 hover:bg-blue-50 cursor-pointer flex items-center justify-between transition-colors" data-testid={`article-result-${article.id}`}>
                        <div>
                          <p className="font-medium text-sm">{article.name}</p>
                          <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                            <span>Cod: {article.code}</span><span>UM: {article.unit || 'buc'}</span>
                            <span className={article.total_stock > 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>Stoc: {article.total_stock || 0}</span>
                          </div>
                        </div>
                        <Plus className="w-5 h-5 text-blue-600" />
                      </div>
                    ))}
                  </div>
                )}
                {consumedItems.length > 0 ? (
                  <div className="space-y-2">
                    {consumedItems.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 rounded-lg border bg-slate-50 gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{item.article_name}</p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-xs text-slate-500">{item.article_code}</p>
                            {item.supplier_name && <Badge variant="outline" className="text-xs py-0">{item.supplier_name}</Badge>}
                            {item.lot_number && <span className="text-xs text-slate-400">Lot: {item.lot_number}</span>}
                            {item.unit_price > 0 && <span className="text-xs text-emerald-600 font-mono">{(item.quantity * item.unit_price).toFixed(2)} RON</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button className="w-8 h-8 rounded-lg border border-slate-300 bg-white flex items-center justify-center text-slate-600 hover:bg-slate-100 font-bold text-lg"
                            onClick={() => handleUpdateConsumedQty(item.article_id, Math.max(0.5, (item.quantity || 1) - 1))} data-testid={`qty-minus-${item.article_id}`}>-</button>
                          <span className="w-12 h-8 flex items-center justify-center text-sm font-semibold text-slate-800 bg-white border border-slate-200 rounded-lg" data-testid={`qty-value-${item.article_id}`}>{item.quantity}</span>
                          <button className="w-8 h-8 rounded-lg border border-slate-300 bg-white flex items-center justify-center text-slate-600 hover:bg-slate-100 font-bold text-lg"
                            onClick={() => handleUpdateConsumedQty(item.article_id, (item.quantity || 0) + 1)} data-testid={`qty-plus-${item.article_id}`}>+</button>
                          <span className="text-xs text-slate-500 w-8 ml-1">{item.unit}</span>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleRemoveConsumedItem(item.article_id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                        </div>
                      </div>
                    ))}
                    {consumedItems.some(i => i.unit_price > 0) && (
                      <div className="flex justify-between items-center p-2 bg-blue-50 rounded-lg border border-blue-200">
                        <span className="text-sm font-medium text-blue-800">Cost total piese:</span>
                        <span className="text-sm font-bold text-blue-900 font-mono">{consumedItems.reduce((sum, i) => sum + (i.quantity || 0) * (i.unit_price || 0), 0).toFixed(2)} RON</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6 border rounded-lg border-dashed border-slate-300">
                    <Package className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                    <p className="text-sm text-slate-500">Cautati sau scanati QR pentru a adauga piese</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Closed ticket consumed items (read-only) */}
          {!isEditable && ticket.consumed_items?.length > 0 && (
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Package className="w-4 h-4 text-blue-500" /> Materiale Consumate ({ticket.consumed_items.length})</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {ticket.consumed_items.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-lg border bg-slate-50">
                      <div><p className="font-medium text-sm">{item.article_name}</p><p className="text-xs text-slate-500">{item.article_code}</p></div>
                      <span className="font-semibold text-sm">{item.quantity} {item.unit}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Signatures */}
          {ticket.status === 'closed' && (ticket.technician_signature || ticket.operator_signature) && (
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Pen className="w-4 h-4 text-slate-500" /> Semnaturi Inchidere</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {ticket.technician_signature && (
                    <div>
                      <p className="text-sm font-medium text-slate-600 mb-2">Semnatura Tehnician</p>
                      <div className="border rounded-lg p-2 bg-white"><img src={ticket.technician_signature} alt="Semnatura tehnician" className="max-h-24 mx-auto" /></div>
                      {ticket.closed_by_name && <p className="text-xs text-slate-500 mt-1">Inchis de: {ticket.closed_by_name}</p>}
                    </div>
                  )}
                  {ticket.operator_signature && !ticket.operator_signature_skipped && (
                    <div>
                      <p className="text-sm font-medium text-slate-600 mb-2">Semnatura Operator</p>
                      <div className="border rounded-lg p-2 bg-white"><img src={ticket.operator_signature} alt="Semnatura operator" className="max-h-24 mx-auto" /></div>
                    </div>
                  )}
                  {ticket.operator_signature_skipped && (
                    <div>
                      <p className="text-sm font-medium text-slate-600 mb-2">Semnatura Operator</p>
                      <div className="border rounded-lg p-3 bg-amber-50 text-center">
                        <AlertTriangle className="w-5 h-5 text-amber-500 mx-auto mb-1" />
                        <p className="text-sm text-amber-700">{ticket.operator_skip_reason === 'not_present' ? 'Operator absent' : 'Operator a refuzat semnarea'}</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Closure Images */}
          {ticket.status === 'closed' && ticket.closure_images?.length > 0 && (
            <Card data-testid="closure-images-card">
              <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Camera className="w-4 h-4 text-green-500" /> Dovada Fotografica Inchidere ({ticket.closure_images.length})</CardTitle></CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {ticket.closure_images.map((img, index) => (
                    <img key={index} src={img.startsWith('http') ? img : `${process.env.REACT_APP_BACKEND_URL}${img}`} alt={`Dovada ${index + 1}`}
                      className="w-28 h-28 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => window.open(img.startsWith('http') ? img : `${process.env.REACT_APP_BACKEND_URL}${img}`, '_blank')} data-testid={`closure-image-${index}`} />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Revision History */}
          {ticket.revision_history?.length > 0 && (
            <Card data-testid="revision-history-card" className="border-amber-200">
              <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4 text-amber-500" /> Istoric Revizuiri ({ticket.revision_count || ticket.revision_history.length})</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[...ticket.revision_history].reverse().map((rev, idx) => (
                    <div key={rev.id || idx} className="p-3 rounded-lg border bg-amber-50/50 border-amber-200" data-testid={`revision-entry-${idx}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold text-amber-800">Revizuire #{ticket.revision_history.length - idx}</span>
                        <span className="text-xs text-slate-500">{new Date(rev.revised_at).toLocaleString('ro-RO')}</span>
                      </div>
                      <p className="text-xs text-slate-600 mb-1">De: <span className="font-medium">{rev.revised_by_name}</span></p>
                      <p className="text-sm text-amber-900"><strong>Motiv:</strong> {rev.reason}</p>
                      {rev.changes && Object.keys(rev.changes).length > 0 && (
                        <div className="mt-2 text-xs text-slate-500">
                          Campuri modificate: {Object.keys(rev.changes).map(k => ({ consumed_items: 'Materiale consumate', notes: 'Note', closure_images: 'Fotografii' }[k] || k)).join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notes */}
          {ticket.notes && (
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">Note</CardTitle></CardHeader>
              <CardContent><div className="bg-slate-50 p-4 rounded-lg whitespace-pre-wrap text-sm text-slate-700">{ticket.notes}</div></CardContent>
            </Card>
          )}

          {/* Rejection reason */}
          {ticket.rejection_reason && (
            <Card className="border-red-200">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-500 mt-0.5" />
                  <div><p className="font-medium text-red-700 text-sm">Motiv Respingere</p><p className="text-red-600 mt-1">{ticket.rejection_reason}</p></div>
                </div>
              </CardContent>
            </Card>
          )}

          <TicketStatusTimeline statusHistory={ticket.status_history} />
        </div>

        {/* RIGHT: Sidebar */}
        <div className="space-y-4">
          <TicketActionsCard
            ticket={ticket} hasRole={hasRole} hasPermission={hasPermission}
            onQuickAccept={quickAccept} onQuickStatusChange={quickStatusChange}
            onReject={() => { setRejectData({ rejection_reason: '', notes: '' }); setShowRejectDialog(true); }}
            onReassign={() => { setReassignData({ new_assignee_id: '', notes: '' }); setShowReassignDialog(true); }}
            onTransform={() => { setTransformData({ scheduled_date: new Date().toISOString().split('T')[0], procedure_id: '', assigned_to: ticket.assigned_to || '', estimated_hours: '' }); setShowTransformDialog(true); }}
            onClose={() => { setCloseData({ notes: '', operatorSkipped: false, skipReason: '' }); setShowCloseDialog(true); }}
            onDelete={handleDelete}
            onRevise={openReviseDialog}
            onEditPartsEta={openEditPartsEta}
          />
          <TicketInfoSidebar ticket={ticket} onEditPartsEta={openEditPartsEta} canEdit={isEditable && (hasPermission ? hasPermission('tickets.write') : hasRole('manager', 'admin', 'technician'))} />
        </div>
      </div>

      {/* Dialogs */}
      <TicketDialogs
        ticket={ticket} ticketId={id} statusCfg={statusCfg}
        showRejectDialog={showRejectDialog} setShowRejectDialog={setShowRejectDialog} rejectData={rejectData} setRejectData={setRejectData} onReject={handleReject}
        showStatusDialog={showStatusDialog} setShowStatusDialog={setShowStatusDialog} statusChangeData={statusChangeData} setStatusChangeData={setStatusChangeData} onStatusChange={handleStatusChange} getAvailableStatuses={getAvailableStatuses}
        showPartsEtaDialog={showPartsEtaDialog} setShowPartsEtaDialog={setShowPartsEtaDialog} partsEtaData={partsEtaData} setPartsEtaData={setPartsEtaData} onPartsEtaConfirm={handlePartsEtaConfirm} partsEtaMode={partsEtaMode}
        showReassignDialog={showReassignDialog} setShowReassignDialog={setShowReassignDialog} reassignData={reassignData} setReassignData={setReassignData} onReassign={handleReassign} technicians={technicians}
        showTransformDialog={showTransformDialog} setShowTransformDialog={setShowTransformDialog} transformData={transformData} setTransformData={setTransformData} onTransform={handleTransform} procedures={procedures}
        showCloseDialog={showCloseDialog} setShowCloseDialog={setShowCloseDialog} closeData={closeData} setCloseData={setCloseData} consumedItems={consumedItems} onClose={handleClose}
        pendingArticle={pendingArticle} showLotSelector={showLotSelector} setShowLotSelector={setShowLotSelector} availableLots={availableLots} selectedLot={selectedLot} setSelectedLot={setSelectedLot}
        pendingQty={pendingQty} setPendingQty={setPendingQty} lotsLoading={lotsLoading} onConfirmAddArticle={confirmAddArticle} onCancelPending={() => { setPendingArticle(null); setSelectedLot(null); }}
        binScanOpen={binScanOpen} setBinScanOpen={setBinScanOpen} binScanCode={binScanCode} binScanType={binScanType} onBinArticleSelect={handleBinArticleSelect}
        showQrScanner={showQrScanner} onStopQrScanner={async () => { await stopQrHook(); setShowQrScanner(false); }}
        showReviseDialog={showReviseDialog} setShowReviseDialog={setShowReviseDialog} reviseData={reviseData} setReviseData={setReviseData}
        reviseConsumedItems={reviseConsumedItems} setReviseConsumedItems={setReviseConsumedItems} reviseClosureImages={reviseClosureImages} setReviseClosureImages={setReviseClosureImages}
        onRevise={handleRevise}
      />

      {/* Mobile Close Wizard */}
      {showMobileWizard && (
        <MobileCloseWizard
          ticket={ticket}
          onCancel={() => setShowMobileWizard(false)}
          onClose={async (data) => {
            try {
              await api.post(`/tickets/${ticket.id}/close`, {
                technician_signature: data.technician_signature, operator_signature: data.operator_signature,
                operator_signature_skipped: data.operator_signature_skipped, operator_skip_reason: data.operator_skip_reason,
                notes: data.notes, consumed_items: ticket.consumed_items || [], closure_images: data.closure_images || [],
              });
              toast.success('Tichet inchis cu succes!'); setShowMobileWizard(false); fetchTicket();
            } catch (err) { toast.error(err.response?.data?.detail || 'Eroare la inchidere'); }
          }}
        />
      )}
    </div>
  );
};

export default TicketDetailPage;
